import { createContext, useContext, useEffect } from 'react';

/**
 * Which screen a tester is on, for the tester feedback button (crossmarc/steward-app#2531,
 * TEMPORARY: `packages/steward-types/src/testerFeedback.ts` says what to remove with it).
 *
 * A screen names itself with {@link useTesterFeedbackScreen} (or the {@link TesterFeedbackScreen}
 * element, for a render branch that cannot call a hook), and the button reads the latest name
 * when it opens. A ref, not state: naming a screen must never re-render the form.
 *
 * Outside a {@link TesterFeedbackScreenContext} provider both are no-ops, so a screen that names
 * itself costs nothing for a firm without the button. This module imports nothing but React,
 * because the intake form imports it (`intake/intakeBundleImports.test.ts`).
 */

export interface TesterFeedbackScreenState {
  /** The screen as the code names it, e.g. `Intake form: Cash accounts`. Empty when unnamed. */
  screen: string;
  /** The signed-in login's address as the browser holds it, when the screen knows it. */
  email: string | null;
}

export type TesterFeedbackScreenRef = { current: TesterFeedbackScreenState };

export const TesterFeedbackScreenContext = createContext<TesterFeedbackScreenRef | null>(null);

export function useTesterFeedbackScreen(screen: string, email?: string | null): void {
  const ref = useContext(TesterFeedbackScreenContext);
  useEffect(() => {
    if (!ref) return;
    ref.current = { screen, email: email ?? null };
    // Cleared on the way out only if still ours, so the next screen's name is never wiped by
    // the previous screen unmounting after it.
    return () => {
      if (ref.current.screen === screen) ref.current = { screen: '', email: null };
    };
  }, [ref, screen, email]);
}

/** {@link useTesterFeedbackScreen} as an element, for one branch of a render. Draws nothing. */
export function TesterFeedbackScreen({ name, email }: { name: string; email?: string | null }): null {
  useTesterFeedbackScreen(name, email);
  return null;
}
